/**
 * Loads Razorpay's hosted Checkout script, once.
 *
 * Checkout is a script on Razorpay's own domain rather than something bundled
 * here, and that is the point: card numbers and UPI IDs are typed into their
 * iframe, not into this app, so nothing sensitive ever reaches TUBMarket's code
 * or its server. What comes back is a payment id and a signature over it.
 *
 * Loaded on demand rather than in index.html: most visitors never check out, and
 * a third-party script on every page view is a cost paid by all of them for the
 * few who do.
 *
 * Resolves false rather than rejecting, because every caller has the same
 * question — "can I open Checkout?" — and a blocked script (ad blocker, offline,
 * strict extension) is an ordinary answer to it, not an exception.
 */

const SCRIPT_ID = 'razorpay-checkout-script';
const SCRIPT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

export function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') {
      resolve(false);
      return;
    }
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    // A second checkout attempt in the same session finds the tag already there.
    // Attaching to it is not the same as resolving true: the first load may
    // still be in flight, or may have failed.
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', () => resolve(Boolean(window.Razorpay)));
      existing.addEventListener('error', () => resolve(false));
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve(Boolean(window.Razorpay));
    script.onerror = () => {
      // Left in the DOM on purpose — the listener branch above reuses the tag,
      // and removing it would let a blocked page retry the same blocked request
      // on every attempt.
      console.error('Failed to load Razorpay Checkout.');
      resolve(false);
    };
    document.body.appendChild(script);
  });
}

/** Razorpay quotes every amount in subunits — paise for INR, cents for USD. */
export const toSubunits = (amount) => Math.round(amount * 100);

/**
 * Opens Razorpay Checkout and settles when the shopper is done with it.
 *
 * Wrapped as a promise because Checkout reports three different endings through
 * three different channels — a success handler, a `payment.failed` event and a
 * dismiss callback — and a caller only wants to know which one happened:
 *
 *   {status: 'paid',      response}  the signed payload to verify server-side
 *   {status: 'failed',    error}     Razorpay refused the payment
 *   {status: 'dismissed'}            the shopper closed the modal
 *
 * Note that 'paid' means Checkout says so. It is not a purchase until the
 * server has verified the signature — see api/payments.js.
 */
export function openRazorpayCheckout(options) {
  return new Promise((resolve) => {
    // Checkout can call more than one of these on the way out — a failure
    // followed by a dismiss is routine — and the first ending is the real one.
    let settled = false;
    const settle = (result) => {
      if (settled) return;
      settled = true;
      resolve(result);
    };

    const checkout = new window.Razorpay({
      ...options,
      handler: (response) => settle({ status: 'paid', response }),
      modal: { ...options.modal, ondismiss: () => settle({ status: 'dismissed' }) },
    });

    checkout.on('payment.failed', (event) => settle({ status: 'failed', error: event?.error }));
    checkout.open();
  });
}
